import React, { useEffect, useState } from 'react'
import { Else, If, Then } from 'react-if'
import { ResponsiveObject } from 'react-slick'
import { Image } from '@/atoms/Image'
import { AsH1, TitleLG } from '@/atoms/Text'
import { HorizontalItemScroller } from '@/molecules/HorizontalItemScroller'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { getNowStreamingProjects, NowStreamingContent } from '@/services/NowStreamingService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { isDefined } from '@/utils/types'

const defaultResponsiveSlick: ResponsiveObject[] = [
  {
    breakpoint: 1536,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToScroll: 5,
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1024,
    settings: {
      slidesToScroll: 4,
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToScroll: 3,
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 640,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
]
export const GuildNowStreamingRail: React.FC<{ featuredSlugs?: string[]; title?: string }> = ({
  featuredSlugs,
  title,
}) => {
  const { t } = useTranslate('guild')
  const [isLoaded, setIsLoaded] = useState(false)
  const [nowStreamingProjects, setNowStreamingProjects] = useState<NowStreamingContent[]>([])

  useEffect(() => {
    if (!isLoaded) {
      fetchNowStreamingProjects(featuredSlugs, setNowStreamingProjects, setIsLoaded)
    }
  }, [isLoaded, featuredSlugs])

  return renderContent(isLoaded, nowStreamingProjects, title, t)
}

const fetchNowStreamingProjects = async (
  featuredSlugs: string[] | undefined,
  setNowStreamingProjects: React.Dispatch<React.SetStateAction<NowStreamingContent[]>>,
  setIsLoaded: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  try {
    const response = await getNowStreamingProjects()
    const definedProjects = response?.filter(isDefined).sort(sortNowStreaming(featuredSlugs))
    setNowStreamingProjects(definedProjects ?? [])
  } catch (e) {
    // Do nothing. If an error occurs, our apollo client will log it and we will just not show this rail
  } finally {
    setIsLoaded(true)
  }
}

const renderContent = (
  isLoaded: boolean,
  nowStreamingProjects: NowStreamingContent[],
  title: string | undefined,
  t: TranslateFunction,
) => {
  if (isLoaded && (!nowStreamingProjects || nowStreamingProjects.length === 0)) return null

  return (
    <div>
      <header>
        <TitleLG color="white" weight="bold" as={AsH1} className="mb-3 md:mb-4">
          <If condition={title}>
            <Then>{title}</Then>
            <Else>
              <Translate t={t} i18nKey="nowStreaming">
                Now Streaming
              </Translate>
            </Else>
          </If>
        </TitleLG>
      </header>

      <HorizontalItemScroller slidesToShow={6} slidesToScroll={6} responsive={defaultResponsiveSlick} arrows>
        {!isLoaded
          ? Array(7)
              .fill(0)
              .map((v, i) => (
                <PlaceholderMediaCard
                  className="mr-4"
                  borderRadiusClassName="rounded-lg"
                  hideTextPlaceholders
                  orientation="portrait"
                  key={i}
                />
              ))
          : nowStreamingProjects?.map((item) => {
              if (!item) return null
              if (!item?.image?.cloudinaryPath) return null
              const title = item.project.name ?? undefined

              return (
                <div key={item.id}>
                  <div className="mb-2 mr-4">
                    <Image
                      aria-label={title}
                      alt={title}
                      loading="lazy"
                      src={getCloudinaryImageUrl({
                        path: item?.image?.cloudinaryPath,
                        width: 300,
                        height: 450,
                      })}
                      height={450}
                      width={300}
                      className="rounded-lg bg-black"
                    />
                  </div>
                </div>
              )
            })}
      </HorizontalItemScroller>
    </div>
  )
}

const sortNowStreaming = (featuredSlugs?: string[]) => (a: NowStreamingContent, b: NowStreamingContent) => {
  if (featuredSlugs && featuredSlugs.length > 0) {
    if (featuredSlugs?.indexOf(a.project.slug as string) > featuredSlugs?.indexOf(b.project.slug as string)) {
      return -1
    } else if (featuredSlugs?.indexOf(a.project.slug as string) < featuredSlugs?.indexOf(b.project.slug as string)) {
      return 1
    }
  }
  return a?.project?.releaseDate > b?.project?.releaseDate ? -1 : 1
}
